import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { driverAPI } from '../lib/api';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
    Search, Users, Edit, Loader2, 
    MoreVertical, Filter, Phone, Mail, CreditCard, History, Truck, Calendar, Camera
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const initialFormData = {
    name: '',
    email: '',
    phone: '',
    license_number: '',
    license_expiry: '',
    status: 'available'
};

const statusOptions = ['available', 'on_trip', 'off_duty', 'suspended'];

const StatusBadge = ({ status }) => {
    const styles = {
        available: 'badge-success',
        on_trip: 'badge-warning',
        off_duty: 'badge-default',
        suspended: 'badge-danger'
    };
    const labels = {
        available: 'Available',
        on_trip: 'On Trip',
        off_duty: 'Off Duty',
        suspended: 'Suspended'
    };
    return (
        <span className={`badge ${styles[status] || 'badge-default'}`}>
            {labels[status] || status}
        </span>
    );
};

export default function DriversPage() {
    const { canManage, isAdmin } = useAuth();
    const { t, language } = useLanguage();
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingDriver, setEditingDriver] = useState(null);
    const [formData, setFormData] = useState(initialFormData);
    const [submitting, setSubmitting] = useState(false);
    
    // History modal state
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [selectedDriverHistory, setSelectedDriverHistory] = useState(null);
    const [historyData, setHistoryData] = useState(null);
    const [historyLoading, setHistoryLoading] = useState(false);

    useEffect(() => {
        fetchDrivers();
    }, []);

    const fetchDrivers = async () => {
        try {
            const response = await driverAPI.getAll();
            setDrivers(response.data);
        } catch (error) {
            toast.error('Failed to fetch drivers');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            if (editingDriver) {
                await driverAPI.update(editingDriver.id, formData);
                toast.success('Driver updated successfully');
            } else {
                await driverAPI.create(formData);
                toast.success('Driver created successfully');
            }
            
            setIsDialogOpen(false);
            setEditingDriver(null);
            setFormData(initialFormData);
            fetchDrivers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Operation failed');
        } finally {
            setSubmitting(false);
        }
    };

    const handleEdit = (driver) => {
        setEditingDriver(driver);
        setFormData({
            name: driver.name,
            email: driver.email,
            phone: driver.phone,
            license_number: driver.license_number || '',
            license_expiry: driver.license_expiry || '',
            status: driver.status || 'available'
        });
        setIsDialogOpen(true);
    };

    const handleDialogClose = () => {
        setIsDialogOpen(false);
        setEditingDriver(null);
        setFormData(initialFormData);
    };

    const filteredDrivers = drivers.filter(driver => {
        const matchesSearch = 
            driver.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            driver.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            driver.license_number.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus === 'all' || driver.status === filterStatus;
        return matchesSearch && matchesStatus;
    });

    const isLicenseExpiringSoon = (expiryDate) => {
        const expiry = new Date(expiryDate);
        const today = new Date();
        const daysUntilExpiry = (expiry - today) / (1000 * 60 * 60 * 24);
        return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
    };

    const isLicenseExpired = (expiryDate) => {
        return new Date(expiryDate) < new Date();
    };

    const handleViewHistory = async (driver) => {
        setSelectedDriverHistory(driver);
        setHistoryModalOpen(true);
        setHistoryLoading(true);
        try {
            // Try with driver.id first, then user_id if available
            const driverId = driver.user_id || driver.id;
            const response = await api.get(`/drivers/${driverId}/history?days=30`);
            setHistoryData(response.data);
        } catch (error) {
            toast.error('Failed to fetch driver history');
        } finally {
            setHistoryLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <Loader2 className="w-8 h-8 text-[#1B7EB5] animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="drivers-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">{language === 'el' ? 'Οδηγοί' : 'Drivers'}</h1>
                    <p className="text-zinc-500">{drivers.length} {language === 'el' ? 'συνολικά' : 'total drivers'}</p>
                </div>
                
                {/* Info Box - Drivers are managed via User Management */}
                {canManage() && (
                    <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                        <Users className="w-4 h-4" />
                        <span>
                            {language === 'el' 
                                ? 'Οι οδηγοί προστίθενται από τη Διαχείριση Χρηστών' 
                                : 'Drivers are added via User Management'}
                        </span>
                        <a href="/app/users" className="font-medium underline hover:text-blue-800">
                            {language === 'el' ? 'Μετάβαση' : 'Go'}
                        </a>
                    </div>
                )}
            </div>

            {/* Edit Driver Dialog - Only for editing driver-specific fields */}
            <Dialog open={isDialogOpen} onOpenChange={(open) => open ? setIsDialogOpen(true) : handleDialogClose()}>
                <DialogContent className="bg-white border-slate-200 max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-slate-800">
                            {language === 'el' ? 'Επεξεργασία Οδηγού' : 'Edit Driver'}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                        <div className="p-3 bg-slate-50 rounded-lg mb-4">
                            <p className="text-sm text-slate-600">
                                <strong>{editingDriver?.name}</strong>
                            </p>
                            <p className="text-xs text-slate-500">{editingDriver?.email}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group">
                                <Label className="form-label">{language === 'el' ? 'Αρ. Διπλώματος' : 'License Number'}</Label>
                                <Input
                                    value={formData.license_number}
                                    onChange={(e) => setFormData({...formData, license_number: e.target.value})}
                                    className="bg-white border-slate-300 text-slate-900"
                                    placeholder="DL-123456"
                                    data-testid="driver-license-input"
                                />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">{language === 'el' ? 'Λήξη Διπλώματος' : 'License Expiry'}</Label>
                                <Input
                                    type="date"
                                    value={formData.license_expiry}
                                    onChange={(e) => setFormData({...formData, license_expiry: e.target.value})}
                                    className="bg-white border-slate-300 text-slate-900"
                                    data-testid="driver-expiry-input"
                                />
                            </div>
                        </div>
                        <div className="form-group">
                            <Label className="form-label">{language === 'el' ? 'Κατάσταση' : 'Status'}</Label>
                            <Select 
                                value={formData.status} 
                                onValueChange={(value) => setFormData({...formData, status: value})}
                            >
                                <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="driver-status-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-white border-slate-200">
                                    {statusOptions.map(status => (
                                        <SelectItem key={status} value={status} className="capitalize">
                                            {status.replace('_', ' ')}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex justify-end gap-3 pt-4">
                            <Button 
                                type="button" 
                                variant="outline" 
                                onClick={handleDialogClose}
                                className="border-slate-300 text-slate-600 hover:text-slate-800"
                            >
                                {language === 'el' ? 'Ακύρωση' : 'Cancel'}
                            </Button>
                            <Button 
                                type="submit" 
                                className="bg-[#1B7EB5] hover:bg-[#58A6B4] text-white"
                                disabled={submitting}
                                data-testid="driver-submit-btn"
                            >
                                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (language === 'el' ? 'Ενημέρωση' : 'Update')}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative sm:w-1/2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                        placeholder="Search by name, email, or license number..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 bg-white border-slate-300 text-slate-900"
                        data-testid="driver-search-input"
                    />
                </div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="sm:w-1/2 bg-white border-slate-300 text-slate-900" data-testid="driver-filter-status">
                        <Filter className="w-4 h-4 mr-2" />
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="all">All Status</SelectItem>
                        {statusOptions.map(status => (
                            <SelectItem key={status} value={status} className="capitalize">
                                {status.replace('_', ' ')}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Table */}
            <div className="fleet-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Driver</th>
                                <th>Contact</th>
                                <th>License</th>
                                <th>Expiry</th>
                                <th>Status</th>
                                {canManage() && <th className="w-12">Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredDrivers.length === 0 ? (
                                <tr>
                                    <td colSpan={canManage() ? 6 : 5} className="text-center py-8">
                                        <div className="empty-state">
                                            <Users className="empty-state-icon mx-auto" />
                                            <p>No drivers found</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filteredDrivers.map((driver) => (
                                    <tr key={driver.id} className="table-row-hover" data-testid={`driver-row-${driver.id}`}>
                                        <td>
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 bg-zinc-800 rounded-sm flex items-center justify-center text-[#1B7EB5] font-medium">
                                                    {driver.name.charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <p className="font-medium text-slate-800">{driver.name}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="space-y-1">
                                                <div className="flex items-center gap-2 text-sm">
                                                    <Mail className="w-3 h-3 text-zinc-500" />
                                                    <span className="text-zinc-400">{driver.email}</span>
                                                </div>
                                                <div className="flex items-center gap-2 text-sm">
                                                    <Phone className="w-3 h-3 text-zinc-500" />
                                                    <span className="text-zinc-400">{driver.phone}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="flex items-center gap-2">
                                                <CreditCard className="w-4 h-4 text-zinc-500" />
                                                <code className="font-mono text-sm">{driver.license_number}</code>
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`text-sm ${
                                                isLicenseExpired(driver.license_expiry) 
                                                    ? 'text-red-500' 
                                                    : isLicenseExpiringSoon(driver.license_expiry)
                                                        ? 'text-[#1B7EB5]'
                                                        : 'text-zinc-400'
                                            }`}>
                                                {new Date(driver.license_expiry).toLocaleDateString()}
                                                {isLicenseExpired(driver.license_expiry) && ' (Expired)'}
                                                {isLicenseExpiringSoon(driver.license_expiry) && ' (Soon)'}
                                            </span>
                                        </td>
                                        <td><StatusBadge status={driver.status} /></td>
                                        {canManage() && (
                                            <td>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-slate-700">
                                                            <MoreVertical className="w-4 h-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" className="bg-white border-slate-200">
                                                        <DropdownMenuItem 
                                                            onClick={() => handleEdit(driver)}
                                                            className="cursor-pointer"
                                                            data-testid={`edit-driver-${driver.id}`}
                                                        >
                                                            <Edit className="w-4 h-4 mr-2" />
                                                            {language === 'el' ? 'Επεξεργασία' : 'Edit'}
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem 
                                                            onClick={() => handleViewHistory(driver)}
                                                            className="cursor-pointer"
                                                            data-testid={`history-driver-${driver.id}`}
                                                        >
                                                            <History className="w-4 h-4 mr-2" />
                                                            {language === 'el' ? 'Ιστορικό' : 'View History'}
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </td>
                                        )}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Driver History Modal */}
            <Dialog open={historyModalOpen} onOpenChange={setHistoryModalOpen}>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-slate-800 flex items-center gap-2">
                            <History className="w-5 h-5 text-[#1B7EB5]" />
                            Driver History - {selectedDriverHistory?.name}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {historyLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 text-[#1B7EB5] animate-spin" />
                        </div>
                    ) : historyData ? (
                        <div className="space-y-6 mt-4">
                            {/* Stats */}
                            <div className="grid grid-cols-3 gap-4">
                                <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                                    <p className="text-2xl font-bold text-[#1B7EB5]">{historyData.total_inspections}</p>
                                    <p className="text-xs text-zinc-500">Check-ins</p>
                                </div>
                                <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                                    <p className="text-2xl font-bold text-[#58A6B4]">{historyData.unique_vehicles}</p>
                                    <p className="text-xs text-zinc-500">Vehicles Used</p>
                                </div>
                                <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                                    <p className="text-2xl font-bold text-[#8CC774]">{historyData.period_days}</p>
                                    <p className="text-xs text-zinc-500">Days</p>
                                </div>
                            </div>

                            {/* Vehicle Usage Summary */}
                            {historyData.vehicle_usage?.length > 0 && (
                                <div>
                                    <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                                        <Truck className="w-4 h-4 text-[#58A6B4]" />
                                        Vehicles Used by This Driver
                                    </h4>
                                    <div className="space-y-2">
                                        {historyData.vehicle_usage.map((vehicle, index) => (
                                            <div key={vehicle.vehicle_id} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 bg-[#1B7EB5]/20 rounded-full flex items-center justify-center text-[#1B7EB5] font-bold text-sm">
                                                        {index + 1}
                                                    </div>
                                                    <div>
                                                        <p className="text-white font-medium">{vehicle.vehicle_info}</p>
                                                        <p className="text-xs text-zinc-500">
                                                            {vehicle.first_use} - {vehicle.last_use || 'Present'}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-[#8CC774] font-semibold">{vehicle.checkin_count}</p>
                                                    <p className="text-xs text-zinc-500">check-ins</p>
                                                    {vehicle.distance_km > 0 && (
                                                        <p className="text-xs text-[#1B7EB5]">{vehicle.distance_km.toLocaleString()} km</p>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Recent Inspections */}
                            {historyData.inspections?.length > 0 && (
                                <div>
                                    <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                                        <Camera className="w-4 h-4 text-[#8CC774]" />
                                        Recent Check-ins
                                    </h4>
                                    <div className="space-y-2 max-h-64 overflow-y-auto">
                                        {historyData.inspections.slice(0, 10).map((inspection) => (
                                            <div key={inspection.id} className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-lg border border-zinc-800">
                                                <div className="flex items-center gap-3">
                                                    <Calendar className="w-4 h-4 text-zinc-500" />
                                                    <div>
                                                        <p className="text-white text-sm">{inspection.date}</p>
                                                        <p className="text-xs text-zinc-500">{inspection.vehicle_info}</p>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-[#1B7EB5] font-medium">{inspection.odometer_reading?.toLocaleString()} km</p>
                                                    {inspection.has_photos && (
                                                        <span className="text-xs text-[#8CC774]">Photos attached</span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {historyData.total_inspections === 0 && historyData.total_assignments === 0 && (
                                <div className="text-center py-8 text-zinc-500">
                                    <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                    <p>No history found for the last {historyData.period_days} days</p>
                                </div>
                            )}
                        </div>
                    ) : null}
                </DialogContent>
            </Dialog>
        </div>
    );
}
